import { useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Activity, Clock, Leaf, TrendingUp, TrendingDown,
  ChevronUp, ChevronDown, BarChart2, LineChart, Cpu, Search, List, ArrowRight,
} from "lucide-react";
import PlotlyChart from "../../components/shared/Viz/PlotlyChart";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { useTheme } from "../../context/ThemeProvider";
import { getColorSchemePalette } from "../../context/colorSchemes";
import { SparkSvg, SparkDarkSvg } from "../../assets";

import Input from "../../components/Inputs/Input";
import { useFinOpsSummary } from "../../api/queryHooks";

// ─── Period tabs ────────────────────────────────────────────────────────────────
const PERIODS = ["7d", "30d", "90d", "12m", "Custom"];

// Colour palette applied to model / request-type breakdowns in order.
const PALETTE = ["#265DF5", "#5DC9F0", "#7C3AED", "#F59E0B", "#EC4899", "#16A34A", "#EF4444", "#8B5CF6"];

// Friendly labels for request_type values returned by the backend.
const REQ_TYPE_LABELS = { chat: "Chat", agent: "Agent", media: "Media", space: "Space", general: "General" };

const PERIOD_SUB = { "7d": "last 7 days", "30d": "last 30 days", "90d": "last 90 days", "12m": "last 12 months", Custom: "custom range" };

// Build a 14-day linear forecast from an actual daily series.
function buildForecast(dates, actual) {
  if (!actual || actual.length === 0) {
    return { dates: [], actual: [], forecastDates: [], forecastValues: [], forecastLow: [], forecastHigh: [] };
  }
  const n = actual.length;
  const recentCount = Math.min(7, n);
  const recent = actual.slice(-recentCount);
  const slope = recentCount > 1 ? (recent[recentCount - 1] - recent[0]) / (recentCount - 1) : 0;
  const last = actual[n - 1];
  const lastDate = new Date(dates[dates.length - 1]);
  const forecastDates = [];
  const forecastValues = [];
  const forecastLow = [];
  const forecastHigh = [];
  for (let i = 1; i <= 14; i++) {
    const d = new Date(lastDate);
    d.setDate(d.getDate() + i);
    forecastDates.push(d.toISOString().slice(0, 10));
    const v = Math.max(0, Math.round(last + slope * i));
    forecastValues.push(v);
    forecastLow.push(Math.round(v * 0.88));
    forecastHigh.push(Math.round(v * 1.12));
  }
  return { dates, actual, forecastDates, forecastValues, forecastLow, forecastHigh };
}

// ─── Metric card ──────────────────────────────────────────────────────────────
const MetricCard = ({ icon: Icon, iconNode, label, value, sub, delta, deltaPositive }) => (
  <div className="photon-content-card bg-surface-card-deep border border-edge-gray-200 dark:border-edge-dialog-surface rounded-xl p-4 flex flex-col gap-1">
    <div className="flex items-center justify-between">
      <span className="text-[11px] font-semibold uppercase tracking-widest text-ink-gray-500 dark:text-ink-gray-400">{label}</span>
      <span className="photon-settings-icon w-7 h-7 rounded-lg bg-fill-blue-50 dark:bg-fill-primary/10 flex items-center justify-center">
        {iconNode ?? <Icon className="w-3.5 h-3.5 text-ink-primary dark:text-ink-accent" />}
      </span>
    </div>
    <p className="text-[26px] font-bold text-ink-gray-900 dark:text-ink-white leading-tight">{value}</p>
    <div className="flex items-center gap-2">
      {delta !== undefined && (
        <span className={`text-xs font-semibold flex items-center gap-0.5 ${deltaPositive ? "text-ink-green-600" : "text-ink-red-600"}`}>
          {deltaPositive ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {Math.abs(delta)}%
        </span>
      )}
      {sub && <span className="text-[11px] text-ink-gray-400">{sub}</span>}
    </div>
  </div>
);

// ─── Main page ────────────────────────────────────────────────────────────────
const FinOpsPage = () => {
  const [period, setPeriod] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [sortCol, setSortCol] = useState("units");
  const [sortDir, setSortDir] = useState("desc");
  const [chartType, setChartType] = useState("area");
  const [userSearch, setUserSearch] = useState("");
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [modelView, setModelView] = useState("chart");
  const [reqView, setReqView] = useState("chart");
  const [selectedUser, setSelectedUser] = useState(null);
  const searchInputRef = useRef(null);
  const { theme, colorScheme } = useTheme();
  const dark = theme === "dark";

  // ─── Live data ────────────────────────────────────────────────────────────
  const queryParams = useMemo(() => (
    period === "Custom"
      ? { period: "Custom", from: customFrom, to: customTo }
      : { period }
  ), [period, customFrom, customTo]);

  const customReady = period !== "Custom" || (!!customFrom && !!customTo);
  const { data, isLoading, isError } = useFinOpsSummary(queryParams, { enabled: customReady });

  const summary = data || {};
  const totals = summary.totals || {};

  const modelUnits = useMemo(() =>
    (summary.by_model || []).map(m => ({
      model: m.model_id,
      units: m.units,
      tokens: m.tokens || 0,
      cachedTokens: m.cached_tokens || 0,
    }))
  , [summary.by_model]);

  const reqTypes = useMemo(() =>
    (summary.by_request_type || []).map((r, i) => ({ label: REQ_TYPE_LABELS[r.type] || r.type, value: r.requests, color: PALETTE[i % PALETTE.length] }))
  , [summary.by_request_type]);

  // When a user is selected, override model + donut data with their personal breakdown
  const activeModelUnits = useMemo(() =>
    selectedUser
      ? selectedUser.by_model.map(m => ({ model: m.model, units: m.units, tokens: m.units, cachedTokens: 0 }))
      : modelUnits
  , [selectedUser, modelUnits]);

  const activeReqTypes = useMemo(() =>
    selectedUser
      ? selectedUser.by_req_type.map((r, i) => ({ label: REQ_TYPE_LABELS[r.type] || r.type, value: r.requests, color: PALETTE[i % PALETTE.length] }))
      : reqTypes
  , [selectedUser, reqTypes]);

  const users = useMemo(() =>
    (summary.users || []).map(u => ({
      display_name: u.display_name || null,
      email: u.email,
      units: u.units,
      requests: u.requests,
      cached_pct: u.cached_pct,
      spaces_count: u.spaces_count ?? 0,
      by_model: u.by_model || [],
      by_req_type: u.by_req_type || [],
    }))
  , [summary.users]);

  const series = useMemo(() => buildForecast(
    (summary.daily || []).map(d => d.date),
    (summary.daily || []).map(d => d.units),
  ), [summary.daily]);

  const palette = getColorSchemePalette(colorScheme, theme);
  const plotBg = palette.background;
  const gridColor = palette.border;
  const fontColor = palette.mutedForeground;
  const lineColor = dark ? "#5DC9F0" : "#265DF5";

  // Trend chart traces
  const trendTraces = useMemo(() => [
    // Forecast confidence band (fill between)
    {
      x: series.forecastDates,
      y: series.forecastHigh,
      type: "scatter", mode: "lines",
      line: { width: 0 },
      showlegend: false,
      hoverinfo: "skip",
      name: "Upper bound",
    },
    {
      x: series.forecastDates,
      y: series.forecastLow,
      type: "scatter", mode: "lines",
      fill: "tonexty",
      fillcolor: dark ? "rgba(93,201,240,0.10)" : "rgba(38,93,245,0.08)",
      line: { width: 0 },
      showlegend: false,
      hoverinfo: "skip",
      name: "Lower bound",
    },
    // Forecast line
    {
      x: series.forecastDates,
      y: series.forecastValues,
      type: "scatter", mode: "lines",
      name: "Forecast",
      line: { color: lineColor, width: 2, dash: "dot" },
      opacity: 0.75,
      hovertemplate: "<extra></extra>",
    },
    // Actual
    {
      x: series.dates,
      y: series.actual,
      type: "scatter", mode: "lines",
      fill: "tozeroy",
      fillcolor: dark ? "rgba(93,201,240,0.07)" : "rgba(38,93,245,0.06)",
      name: "Actual units",
      line: { color: lineColor, width: 2.5 },
      hovertemplate: "<extra></extra>",
    },
  ], [dark, lineColor, series]);

  const trendLayout = useMemo(() => ({
    paper_bgcolor: plotBg,
    plot_bgcolor: plotBg,
    margin: { t: 10, r: 12, b: 40, l: 48 },
    height: 220,
    hovermode: "x",
    font: { color: fontColor, size: 11 },
    legend: { x: 0, y: 1.15, orientation: "h", font: { size: 11 } },
    xaxis: { gridcolor: gridColor, linecolor: gridColor, tickfont: { color: fontColor } },
    yaxis: { gridcolor: gridColor, linecolor: gridColor, tickfont: { color: fontColor }, title: { text: "Units", font: { color: fontColor, size: 11 } } },
    shapes: series.dates.length ? [{
      type: "line",
      x0: series.dates[series.dates.length - 1],
      x1: series.dates[series.dates.length - 1],
      y0: 0, y1: 1,
      yref: "paper",
      line: { color: fontColor, width: 1, dash: "dot" },
    }] : [],
    annotations: series.dates.length ? [{
      x: series.dates[series.dates.length - 1],
      y: 1, yref: "paper",
      text: "Today", showarrow: false,
      font: { color: fontColor, size: 10 },
      xanchor: "left", xshift: 4, yshift: -2,
    }] : [],
  }), [plotBg, gridColor, fontColor, series]);

  // Bar chart traces (no forecast)
  const barTrendTraces = useMemo(() => [{
    x: series.dates,
    y: series.actual,
    type: "bar",
    name: "Daily units",
    marker: { color: lineColor, opacity: 0.75 },
    hovertemplate: "<extra></extra>",
  }], [lineColor, series]);

  // Model bar chart — stacked cached vs non-cached tokens
  const modelTraces = useMemo(() => {
    const models = activeModelUnits.map(m => m.model);
    const actual = activeModelUnits.map(m => m.tokens);
    const cached = activeModelUnits.map(m => m.cachedTokens);
    const hasTokenData = activeModelUnits.some(m => m.tokens > 0 || m.cachedTokens > 0);
    if (!hasTokenData) {
      // Fallback: single bar using units when no token-level data at all
      return [{
        type: "bar", orientation: "h",
        name: "Units",
        x: activeModelUnits.map(m => m.units),
        y: models,
        marker: { color: dark ? "#5DC9F0" : "#265DF5" },
        hovertemplate: "<extra></extra>",
      }];
    }
    return [
      {
        type: "bar", orientation: "h",
        name: "Tokens",
        x: actual,
        y: models,
        marker: { color: dark ? "#5DC9F0" : "#265DF5" },
        hovertemplate: "<extra></extra>",
      },
      {
        type: "bar", orientation: "h",
        name: "Cached tokens",
        x: cached,
        y: models,
        marker: { color: dark ? "#4ADE80" : "#16A34A" },
        hovertemplate: "<extra></extra>",
      },
    ];
  }, [activeModelUnits, dark]);

  const modelChartHeight = Math.max(200, activeModelUnits.length * 30 + 80);

  const modelLayout = useMemo(() => ({
    paper_bgcolor: plotBg, plot_bgcolor: plotBg,
    barmode: "stack",
    hovermode: "y",
    margin: { t: 30, r: 16, b: 36, l: 160 },
    height: modelChartHeight,
    font: { color: fontColor, size: 11 },
    legend: { orientation: "h", x: 0, y: 1.06, yanchor: "bottom", font: { size: 11 } },
    xaxis: { gridcolor: gridColor, linecolor: gridColor, tickfont: { color: fontColor }, title: { text: "Tokens", font: { color: fontColor, size: 11 } } },
    yaxis: { linecolor: gridColor, tickfont: { color: fontColor }, automargin: true },
  }), [plotBg, gridColor, fontColor, modelChartHeight]);

  // Donut
  const donutTrace = useMemo(() => [{
    type: "pie",
    hole: 0.6,
    values: activeReqTypes.map(r => r.value),
    labels: activeReqTypes.map(r => r.label),
    marker: { colors: activeReqTypes.map(r => r.color) },
    textinfo: "percent",
    hovertemplate: "<extra></extra>",
    textfont: { color: "#FFFFFF", size: 11 },
  }], [activeReqTypes]);

  const donutLayout = useMemo(() => ({
    paper_bgcolor: plotBg, plot_bgcolor: plotBg,
    margin: { t: 12, r: 12, b: 12, l: 12 },
    height: 280,
    font: { color: fontColor, size: 11 },
    legend: { font: { color: fontColor, size: 11 }, orientation: "v", x: 0.76, y: 0.5 },
    showlegend: true,
  }), [plotBg, fontColor]);

  const sortedUsers = useMemo(() => [...users].sort((a, b) =>
    sortDir === "desc" ? b[sortCol] - a[sortCol] : a[sortCol] - b[sortCol]
  ), [users, sortCol, sortDir]);

  const filteredUsers = useMemo(() =>
    sortedUsers.filter(u => {
      if (!userSearch) return true;
      const q = userSearch.toLowerCase();
      return (u.email || "").toLowerCase().includes(q) ||
             (u.display_name || "").toLowerCase().includes(q);
    })
  , [sortedUsers, userSearch]);

  const handleSort = (col) => {
    if (col === sortCol) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  const totalUnits = totals.units || 0;
  const totalReqs  = totals.requests || 0;
  const avgLatency = totals.avg_latency_ms ? `${(totals.avg_latency_ms / 1000).toFixed(2)}s` : "—";
  const cacheRate  = `${totals.cache_rate_pct ?? 0}%`;
  const periodSub  = PERIOD_SUB[period] || "";
  const lastActual = series.actual.length ? series.actual[series.actual.length - 1] : 0;
  const forecastEnd = series.forecastValues.length ? series.forecastValues[series.forecastValues.length - 1] : 0;
  const forecastGrowth = lastActual ? Math.round(((forecastEnd - lastActual) / lastActual) * 100) : 0;

  const plotConfig = { displayModeBar: false, responsive: true };

  return (
    <SettingsPageShell
      title="FinOps"
      description="Track AI consumption, unit spend, and 14-day usage forecast across your organisation."
      maxWidth="6xl"
    >
      {/* Period selector */}
      <div className="flex items-center gap-1 bg-surface-muted dark:bg-surface-muted rounded-lg p-1 w-fit">
        {PERIODS.map(p => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              period === p
                ? "bg-fill-white dark:bg-fill-deep-surface text-ink-primary dark:text-ink-accent shadow-sm"
                : "text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-gray-900 dark:hover:text-ink-white"
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      {/* Custom date range — slides in below tabs when Custom is selected */}
      <AnimatePresence>
        {period === "Custom" && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="flex items-center gap-2 pt-3 pb-1">
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-gray-500 dark:text-ink-gray-400">From</span>
                <Input
                  type="date"
                  value={customFrom}
                  onChange={e => setCustomFrom(e.target.value)}
                  max={customTo || undefined}
                  className="w-36 text-xs !border-edge-gray-200 dark:!border-edge-dialog-border [color-scheme:light] dark:[color-scheme:dark]"
                />
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-ink-gray-400 mt-4 flex-shrink-0" />
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-gray-500 dark:text-ink-gray-400">To</span>
                <Input
                  type="date"
                  value={customTo}
                  onChange={e => setCustomTo(e.target.value)}
                  min={customFrom || undefined}
                  className="w-36 text-xs !border-edge-gray-200 dark:!border-edge-dialog-border [color-scheme:light] dark:[color-scheme:dark]"
                />
              </div>
              {customFrom && customTo && (
                <button
                  onClick={() => { setCustomFrom(""); setCustomTo(""); }}
                  className="mt-4 text-[11px] text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-primary dark:hover:text-ink-accent transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Headline metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
        <MetricCard iconNode={theme === "dark" ? <SparkDarkSvg className="w-3.5 h-auto" /> : <SparkSvg className="w-3.5 h-auto" />}
                    label="Total Units"    value={totalUnits.toLocaleString()} sub={periodSub} />
        <MetricCard icon={Activity} label="Requests"       value={totalReqs.toLocaleString()}  sub={periodSub} />
        <MetricCard icon={Clock}    label="Avg Latency"    value={avgLatency}                  sub="mean response" />
        <MetricCard icon={Leaf}   label="Cache Savings"  value={cacheRate}                   sub="tokens cached" />
      </div>

      {/* Trend + forecast */}
      <div className="mt-7 mb-3 flex items-end justify-between">
        <div>
          <p className="text-[13px] font-bold text-ink-gray-900 dark:text-ink-white">Unit consumption over time</p>
          <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">
            {chartType === "area"
              ? "Actual (solid) — 14-day forecast (dotted) with ±12% confidence band"
              : "Daily unit consumption as bars"}
          </p>
        </div>
        {/* Area / Bar toggle */}
        <div className="flex items-center gap-1 bg-surface-muted dark:bg-surface-muted rounded-lg p-1">
          <button
            onClick={() => setChartType("area")}
            title="Area chart"
            className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              chartType === "area"
                ? "bg-fill-white dark:bg-fill-deep-surface text-ink-primary dark:text-ink-accent shadow-sm"
                : "text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-gray-900 dark:hover:text-ink-white"
            }`}
          >
            <LineChart className="w-3 h-3" />Area
          </button>
          <button
            onClick={() => setChartType("bar")}
            title="Bar chart"
            className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              chartType === "bar"
                ? "bg-fill-white dark:bg-fill-deep-surface text-ink-primary dark:text-ink-accent shadow-sm"
                : "text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-gray-900 dark:hover:text-ink-white"
            }`}
          >
            <BarChart2 className="w-3 h-3" />Bar
          </button>
        </div>
      </div>
      <div className="bg-surface-card-deep dark:bg-surface-card-deep border border-edge-gray-200 dark:border-edge-dialog-surface rounded-xl p-4">
        {/* Forecast callout — area mode only */}
        {chartType === "area" && (
          <div className={`mb-3 flex items-center gap-2 text-xs px-3 py-2 rounded-lg w-fit ${
            forecastGrowth >= 0
              ? "bg-fill-hex-fff7ed dark:bg-fill-amber-500/10 text-ink-amber-700 dark:text-ink-amber-300"
              : "bg-fill-green-50 dark:bg-fill-green-600/10 text-ink-green-700 dark:text-ink-green-300"
          }`}>
            {forecastGrowth >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
            <strong>14-day forecast:</strong> {forecastGrowth >= 0 ? "+" : ""}{forecastGrowth}% growth in daily units projected. Estimated peak: <strong>{forecastEnd.toLocaleString()} units/day</strong>.
          </div>
        )}
        <div className="overflow-hidden" style={{ height: 220 }}>
          <PlotlyChart
            key={chartType}
            data={chartType === "area" ? trendTraces : barTrendTraces}
            layout={trendLayout}
            config={plotConfig}
            style={{ width: "100%", height: "100%" }}
            useResizeHandler
            isDark={dark}
            tooltipWidth={200}
            onHoverData={(d) => {
              const pts = (d.points || []).filter(p => p.data.hoverinfo !== "skip");
              if (!pts.length) return null;
              const rawDate = pts[0].x;
              const date = new Date(rawDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
              const rows = pts.map(p => ({
                name: p.data.name,
                value: p.y,
                color: p.data.line?.color ?? p.data.marker?.color ?? lineColor,
              }));
              return { date, rows };
            }}
            renderTooltip={(payload, isDk) => (
              <div className="p-3">
                <div className="text-[10px] font-medium text-ink-gray-400 mb-2">{payload.date}</div>
                {payload.rows.map(({ name, value, color }) => (
                  <div key={name} className="flex items-center gap-2 py-0.5">
                    <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} />
                    <span className="flex-1 text-[11px]">{name}</span>
                    <span className={`tabular-nums font-semibold text-[11px] ${isDk ? "text-ink-white" : "text-ink-gray-900"}`}>
                      {Math.round(value).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          />
        </div>
      </div>

      {/* Filter banner */}
      {selectedUser && (
        <div className="mt-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-fill-blue-50 dark:bg-fill-primary/10 border border-edge-blue-200 dark:border-edge-primary/30">
          <span className="w-2 h-2 rounded-full bg-fill-primary dark:bg-fill-accent flex-shrink-0" />
          <span className="text-[12px] font-medium text-ink-blue-700 dark:text-ink-accent flex-1">
            Filtering charts for <span className="font-semibold">{selectedUser.display_name || (selectedUser.email || "").split("@")[0]}</span>
          </span>
          <button
            onClick={() => setSelectedUser(null)}
            className="text-[11px] text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-primary dark:hover:text-ink-accent underline"
          >
            Clear
          </button>
        </div>
      )}

      {/* Model breakdown + request type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {/* Units by model */}
        <div className="bg-surface-card-deep dark:bg-surface-card-deep border border-edge-gray-200 dark:border-edge-dialog-surface rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Cpu className="w-3.5 h-3.5 text-ink-primary dark:text-ink-accent" />
              <p className="text-xs font-semibold text-ink-gray-900 dark:text-ink-white">
                {selectedUser ? `Models · ${selectedUser.display_name || (selectedUser.email || "").split("@")[0]}` : "Units by model"}
              </p>
            </div>
            <button
              onClick={() => setModelView(v => v === "chart" ? "table" : "chart")}
              title={modelView === "chart" ? "Show table" : "Show chart"}
              className="w-6 h-6 rounded-md flex items-center justify-center text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-primary dark:hover:text-ink-accent hover:bg-fill-blue-50 dark:hover:bg-fill-primary/10 transition-colors"
            >
              {modelView === "chart" ? <List className="w-3.5 h-3.5" /> : <BarChart2 className="w-3.5 h-3.5" />}
            </button>
          </div>
          <div style={{ height: modelChartHeight }} className="overflow-hidden">
            {modelView === "chart" ? (
              <PlotlyChart
                data={modelTraces}
                layout={modelLayout}
                config={plotConfig}
                style={{ width: "100%", height: "100%" }}
                useResizeHandler
                isDark={dark}
                tooltipWidth={220}
                onHoverData={(d) => {
                  if (!d.points?.length) return null;
                  const model = d.points[0].y;
                  const rows = d.points
                    .filter(p => p.x > 0)
                    .map(p => ({
                      name: p.data.name,
                      value: p.x,
                      color: typeof p.fullData.marker.color === "string"
                        ? p.fullData.marker.color
                        : (p.fullData.marker.color?.[0] ?? "#265DF5"),
                    }));
                  const tokensRow = d.points.find(p => p.data.name === "Tokens");
                  const cachedRow = d.points.find(p => p.data.name === "Cached tokens");
                  const tokens = tokensRow?.x ?? 0;
                  const cached = cachedRow?.x ?? 0;
                  const cacheRate = tokens > 0 ? Math.round(cached / tokens * 100) : null;
                  const total = tokens + cached;
                  return { model, rows, total, cacheRate };
                }}
                renderTooltip={(payload, isDk) => (
                  <div className="p-3">
                    <div className="text-[10px] font-medium text-ink-gray-400 mb-2 truncate">{payload.model}</div>
                    {payload.rows.map(({ name, value, color }) => (
                      <div key={name} className="flex items-center gap-2 py-0.5">
                        <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} />
                        <span className="flex-1 text-[11px]">{name}</span>
                        <span className={`tabular-nums font-semibold text-[11px] ${isDk ? "text-ink-white" : "text-ink-gray-900"}`}>
                          {value.toLocaleString()}
                        </span>
                      </div>
                    ))}
                    {payload.rows.length > 1 && (
                      <div className={`border-t mt-2 pt-1.5 space-y-0.5 ${isDk ? "border-edge-dialog-surface" : "border-edge-gray-200"}`}>
                        <div className="flex justify-between text-[11px]">
                          <span className="text-ink-gray-400">Total</span>
                          <span className={`font-bold tabular-nums ${isDk ? "text-ink-white" : "text-ink-gray-900"}`}>
                            {payload.total.toLocaleString()}
                          </span>
                        </div>
                        {payload.cacheRate !== null && (
                          <div className="flex justify-between text-[11px]">
                            <span className="text-ink-gray-400">Cache rate</span>
                            <span className="font-semibold tabular-nums text-ink-green-600 dark:text-ink-green-400">
                              {payload.cacheRate}%
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              />
            ) : (
              <div className="overflow-y-auto h-full">
                <table className="w-full text-xs mt-1">
              <thead>
                <tr className="border-b border-edge-gray-100 dark:border-edge-dialog-surface">
                  {["Model", "Units", "Share"].map(h => (
                    <th key={h} className={`py-2 font-semibold uppercase tracking-wider text-ink-gray-500 dark:text-ink-gray-400 ${h === "Model" ? "text-left" : "text-right"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {modelUnits.map((m, i) => {
                  const total = modelUnits.reduce((s, x) => s + x.units, 0);
                  return (
                    <tr key={m.model} className="border-b border-edge-gray-50 dark:border-edge-dialog-surface last:border-0 hover:bg-fill-gray-50 dark:hover:bg-fill-dialog-surface/40">
                      <td className="py-2.5 pr-2">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: m.color }} />
                          <span className="text-ink-gray-900 dark:text-ink-white font-medium">{m.model}</span>
                        </div>
                      </td>
                      <td className="py-2.5 text-right tabular-nums text-ink-gray-700 dark:text-ink-gray-300">{m.units.toLocaleString()}</td>
                      <td className="py-2.5 text-right tabular-nums text-ink-gray-500 dark:text-ink-gray-400">{total ? Math.round(m.units / total * 100) : 0}%</td>
                    </tr>
                  );
                })}
              </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Requests by type */}
        <div className="bg-surface-card-deep dark:bg-surface-card-deep border border-edge-gray-200 dark:border-edge-dialog-surface rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <BarChart2 className="w-3.5 h-3.5 text-ink-primary dark:text-ink-accent" />
              <p className="text-xs font-semibold text-ink-gray-900 dark:text-ink-white">Requests by type</p>
            </div>
            <button
              onClick={() => setReqView(v => v === "chart" ? "table" : "chart")}
              title={reqView === "chart" ? "Show table" : "Show chart"}
              className="w-6 h-6 rounded-md flex items-center justify-center text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-primary dark:hover:text-ink-accent hover:bg-fill-blue-50 dark:hover:bg-fill-primary/10 transition-colors"
            >
              {reqView === "chart" ? <List className="w-3.5 h-3.5" /> : <BarChart2 className="w-3.5 h-3.5" />}
            </button>
          </div>
          <div style={{ height: 280 }} className="overflow-hidden">
            {reqView === "chart" ? (
              <PlotlyChart
                data={donutTrace}
                layout={donutLayout}
                config={plotConfig}
                style={{ width: "100%", height: "100%" }}
                useResizeHandler
                isDark={dark}
                tooltipWidth={170}
                onHoverData={(d) => {
                  if (!d.points?.length) return null;
                  const p = d.points[0];
                  return {
                    label: p.label,
                    value: p.value,
                    percent: Math.round(p.percent * 100),
                    color: p.color,
                  };
                }}
                renderTooltip={(payload, isDk) => (
                  <div className="p-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: payload.color }} />
                      <span className={`text-[11px] font-semibold ${isDk ? "text-ink-white" : "text-ink-gray-900"}`}>{payload.label}</span>
                    </div>
                    <div className="flex justify-between gap-4 text-[11px]">
                      <span className="text-ink-gray-400">{payload.value.toLocaleString()} req</span>
                      <span className={`font-bold tabular-nums ${isDk ? "text-ink-white" : "text-ink-gray-900"}`}>{payload.percent}%</span>
                    </div>
                  </div>
                )}
              />
            ) : (
              <div className="overflow-y-auto h-full">
                <table className="w-full text-xs mt-1">
              <thead>
                <tr className="border-b border-edge-gray-100 dark:border-edge-dialog-surface">
                  {["Type", "Requests", "Share"].map(h => (
                    <th key={h} className={`py-2 font-semibold uppercase tracking-wider text-ink-gray-500 dark:text-ink-gray-400 ${h === "Type" ? "text-left" : "text-right"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reqTypes.map(r => {
                  const total = reqTypes.reduce((s, x) => s + x.value, 0);
                  return (
                    <tr key={r.label} className="border-b border-edge-gray-50 dark:border-edge-dialog-surface last:border-0 hover:bg-fill-gray-50 dark:hover:bg-fill-dialog-surface/40">
                      <td className="py-2.5 pr-2">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: r.color }} />
                          <span className="text-ink-gray-900 dark:text-ink-white font-medium">{r.label}</span>
                        </div>
                      </td>
                      <td className="py-2.5 text-right tabular-nums text-ink-gray-700 dark:text-ink-gray-300">{r.value.toLocaleString()}</td>
                      <td className="py-2.5 text-right tabular-nums text-ink-gray-500 dark:text-ink-gray-400">{total ? Math.round(r.value / total * 100) : 0}%</td>
                    </tr>
                  );
                })}
              </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* User leaderboard */}
      <div className="mt-7 mb-3 flex items-center justify-between">
        <div>
          <p className="text-[13px] font-bold text-ink-gray-900 dark:text-ink-white">Usage by user</p>
          <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">Top consumers by units_consumed this period</p>
        </div>
        {/* Expanding search — grows left from the icon */}
        <div className="flex items-center gap-2">
          <AnimatePresence>
            {showUserSearch && (
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 200, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <Input
                  ref={searchInputRef}
                  autoFocus
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  placeholder="Search users…"
                />
              </motion.div>
            )}
          </AnimatePresence>
          <button
            onClick={() => {
              setShowUserSearch(v => {
                if (v) setUserSearch("");
                return !v;
              });
            }}
            title="Search users"
            className={`w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center transition-colors ${
              showUserSearch
                ? "bg-fill-primary dark:bg-fill-accent text-ink-on-primary"
                : "bg-fill-gray-100 dark:bg-fill-dialog-surface text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-primary dark:hover:text-ink-accent"
            }`}
          >
            <Search className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="bg-surface-card-deep dark:bg-surface-card-deep border border-edge-gray-200 dark:border-edge-dialog-surface rounded-xl overflow-hidden">
        <div className="max-h-[520px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-surface-card-deep dark:bg-surface-card-deep">
            <tr className="border-b border-edge-gray-200 dark:border-edge-dialog-surface">
              {[
                { col: "name",        label: "User",            w: "w-auto" },
                { col: "units",       label: "Units",           w: "w-28 text-right" },
                { col: "requests",    label: "Activity",        w: "w-36 text-right" },
                { col: "cached_pct",  label: "Cache %",         w: "w-20 text-right whitespace-nowrap" },
              ].map(({ col, label, w }) => (
                <th
                  key={col}
                  onClick={() => col !== "name" && handleSort(col)}
                  className={`px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-ink-gray-500 dark:text-ink-gray-400 ${w} ${col !== "name" ? "cursor-pointer select-none hover:text-ink-primary dark:hover:text-ink-accent" : ""}`}
                >
                  {label}
                  {sortCol === col && (
                    <span className="ml-0.5">{sortDir === "desc" ? "↓" : "↑"}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-[12px] text-ink-gray-400">
                  {isLoading ? "Loading usage…" : isError ? "Couldn't load usage data" : userSearch ? `No users match "${userSearch}"` : "No usage recorded for this period"}
                </td>
              </tr>
            ) : filteredUsers.map((u, i) => {
              const topUnits = users[0]?.units || 1;
              const barWidth = Math.round((u.units / topUnits) * 100);
              return (
                <tr
                  key={u.email}
                  onClick={() => setSelectedUser(prev => prev?.email === u.email ? null : u)}
                  className={`border-b border-edge-gray-100 dark:border-edge-dialog-surface last:border-0 cursor-pointer transition-colors ${
                    selectedUser?.email === u.email
                      ? "bg-fill-blue-50 dark:bg-fill-primary/10"
                      : "hover:bg-fill-gray-50 dark:hover:bg-fill-dialog-surface/40"
                  }`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <span className="w-6 h-6 rounded-full bg-fill-blue-50 dark:bg-fill-primary/15 text-ink-primary dark:text-ink-accent text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                        {i + 1}
                      </span>
                      <div>
                        <p className="text-[13px] font-medium text-ink-gray-900 dark:text-ink-white">
                          {u.display_name || (u.email || "").split("@")[0]}
                        </p>
                        <p className="text-[11px] text-ink-gray-400">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-[13px] font-semibold text-ink-gray-900 dark:text-ink-white tabular-nums">{u.units.toLocaleString()}</span>
                      <div className="w-20 h-1 bg-surface-track-muted dark:bg-surface-track-muted rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-fill-primary dark:bg-fill-accent" style={{ width: `${barWidth}%` }} />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-[13px] tabular-nums text-ink-gray-700 dark:text-ink-gray-300">{u.requests.toLocaleString()} req</span>
                      {u.spaces_count > 0 && (
                        <span className="text-[11px] font-medium text-ink-primary dark:text-ink-accent">
                          {u.spaces_count} Spectra{u.spaces_count > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <span className={`text-[12px] font-medium tabular-nums whitespace-nowrap px-2 py-0.5 rounded-full ${
                      u.cached_pct >= 30
                        ? "bg-fill-green-100 text-ink-green-700 dark:bg-fill-green-600/20 dark:text-ink-green-300"
                        : "bg-fill-gray-100 text-ink-gray-500 dark:bg-fill-gray-700 dark:text-ink-gray-400"
                    }`}>{u.cached_pct}%</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </SettingsPageShell>
  );
};

export default FinOpsPage;
